/**
 * Standalone session store replacing AWS Amplify/Cognito. The legacy app used
 * `Auth.currentAuthenticatedUser()` for the bearer token and session; now the
 * token comes from aristotle-backend's `POST /auth/login` and lives in
 * localStorage. APIClient reads it; the auth gate (ClientAppRenderer) and
 * useUser bootstrap from it.
 */
const TOKEN_KEY = 'aristotle_token'
const ORG_SLUG_KEY = 'aristotle_org_slug'
// 30-day "trusted device" token issued by the backend after a 2FA login when
// the user opts to trust this device. Presented on the next login to skip 2FA.
const DEVICE_TOKEN_KEY = 'aristotle_device_token'

const hasWindow = typeof window !== 'undefined'

export const getToken = (): string | null =>
  hasWindow ? window.localStorage.getItem(TOKEN_KEY) : null

export const setToken = (token: string): void => {
  if (hasWindow) window.localStorage.setItem(TOKEN_KEY, token)
}

export const clearToken = (): void => {
  if (hasWindow) window.localStorage.removeItem(TOKEN_KEY)
}

export const isAuthenticated = (): boolean => !!getToken()

/** The org slug (subdomain) the session is bound to, used as the X-Org-Slug login header. */
export const getOrgSlug = (): string | null =>
  hasWindow ? window.localStorage.getItem(ORG_SLUG_KEY) : null

export const setOrgSlug = (slug: string): void => {
  if (hasWindow) window.localStorage.setItem(ORG_SLUG_KEY, slug)
}

/** The trusted-device token for skipping 2FA on this device, if any. */
export const getDeviceToken = (): string | null =>
  hasWindow ? window.localStorage.getItem(DEVICE_TOKEN_KEY) : null

export const setDeviceToken = (token: string): void => {
  if (hasWindow) window.localStorage.setItem(DEVICE_TOKEN_KEY, token)
}

export const clearDeviceToken = (): void => {
  if (hasWindow) window.localStorage.removeItem(DEVICE_TOKEN_KEY)
}

/** Clear the session and return to the login screen. */
export const signOutLocal = (): void => {
  clearToken()
  if (hasWindow) window.location.assign('/')
}
